import { formatDate } from "@angular/common";
import { Injectable } from "@angular/core";
import { ChartLegendItem, ChartTooltipItem } from "chart.js";
import { merge } from "lodash";
import { AirbrakeService } from "../airbrake/airbrake.service";
import { EnvColorService } from "../env-color/env-color.service";
import { formatNumberToHuman } from "../number-to-human/number-to-human";
import { pluralize } from "../utils/pluralize";

@Injectable()
export class ChartHelperService {
  private DATE_FORMAT = "E, LLL d, yyyy UTC";
  private DEPLOY_DATE_FORMAT = "MMM dd, y HH:mm:ss UTC";
  private HOUR_DURATION = 3600000;
  private MINUTE_DURATION = 60000;
  private TICK_DATE_FORMAT = "d MMM";
  private TICK_TIME_FORMAT = "HH:mm";
  private TIME_FORMAT = "E, LLL d, yyyy HH:mm UTC";
  private TIME_HOUR_FORMAT = "E, LLL d, yyyy HH'h' UTC";

  constructor(
    private envColorService: EnvColorService,
    private airbrakeService: AirbrakeService,
  ) {}

  public defaultChartOptions = () => {
    return {
      elements: {
        point: {
          radius: 0,
          hoverRadius: 2,
        },
        line: {
          fill: false,
          borderWidth: 1,
          tension: 0,
        },
        rectangle: {
          borderWidth: 1,
        },
      },
      legend: {
        display: false,
      },
      hover: {
        mode: "index",
        intersect: false,
      },
      tooltips: {
        enabled: false,
        intersect: false,
        callbacks: {
          title: this.dateFormatTooltipTitle,
          label: this.numberToHumanLabel,
        },
        mode: "index",
        custom: this.customTooltip,
        filter: this.filterTooltipItems,
        bodyFontSize: 13, // $ab-font-size-small
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              drawOnChartArea: false,
              color: "rgba(230, 230, 230, 0.5)", // $ab-grey-900
            },
            ticks: {
              autoSkipPadding: 10,
              callback: this.dateFormatTick,
              maxRotation: 0,
              maxTicksLimit: 6,
              fontColor: "#b8b8b8", // $ab-grey-700
              fontSize: 11, // $ab-font-size-x-small
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              color: "rgba(230, 230, 230, 0.5)", // $ab-grey-900
            },
            ticks: {
              callback: this.numberToHumanTick,
              min: 0,
              maxTicksLimit: 6,
              fontColor: "#b8b8b8", // $ab-grey-700
              fontSize: 11, // $ab-font-size-x-small
            },
          },
        ],
      },
      maintainAspectRatio: false,
    };
  };

  public defaultMiniBarChartOptions = () => {
    return merge({}, this.defaultChartOptions(), {
      scales: {
        xAxes: [
          {
            gridLines: {
              drawTicks: false,
            },
            ticks: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              display: false,
              drawTicks: false,
            },
            ticks: {
              display: false,
            },
          },
        ],
      },
    });
  };

  public defaultChartWithDeployAnnotationsOptions = () => {
    return merge({}, this.defaultChartOptions(), {
      legend: {
        display: true,
        onClick: null,
        labels: {
          boxWidth: 12,
          filter(legendItem: ChartLegendItem, _chart: Chart) {
            return !legendItem.text.includes("Deploys");
          },
        },
      },
      hover: {
        intersect: true,
        mode: "nearest",
      },
      tooltips: {
        intersect: true,
        mode: "nearest",
        callbacks: {
          title: this.chartWithDeployAnnotationsTooltipTitle,
          label: this.chartWithDeployAnnotationsTooltipLabel,
        },
      },
    });
  };

  public removeTooltip() {
    document.querySelector(".chartjs-tooltip")?.remove();
  }

  public generateDeployAnnotationsDataset(
    deploys: any[],
    ticks: Date[],
    dur: string,
  ) {
    const borderColors: string[] = [];
    const deploysData: any[] = [];
    const dataCounts: number[] = [];

    let deployDates: number[];
    let tickDates: number[];

    if (dur === "90d/1d") {
      deployDates = deploys.map((d) =>
        new Date(d.createdAt).setHours(0, 0, 0, 0),
      );
      tickDates = ticks.map((tick) => new Date(tick).setHours(0, 0, 0, 0));
    } else if (dur === "90h/1h") {
      deployDates = deploys.map((d) =>
        new Date(d.createdAt).setMinutes(0, 0, 0),
      );
      tickDates = ticks.map((tick) => new Date(tick).setMinutes(0, 0, 0));
    } else if (dur === "90m/1m") {
      deployDates = deploys.map((d) => new Date(d.createdAt).setSeconds(0, 0));
      tickDates = ticks.map((tick) => new Date(tick).setSeconds(0, 0));
    }

    // For each date (tick) in the chart find deploy within that date.
    for (const tickDate of tickDates) {
      const index = deployDates.indexOf(tickDate);

      if (index < 0) {
        // Deploy not found, attach empty data for deploy annotation.
        dataCounts.push(null);
        deploysData.push(null);
        borderColors.push("#ffffff"); // ab-white
      } else {
        // Deploy found, attach data for deploy annotation.
        const d = deploys[index];
        dataCounts.push(0);
        deploysData.push(d);
        borderColors.push(this.envColorService.colorString(d.environment));
      }
    }

    return {
      type: "line",
      showLine: true,
      label: "Deploys",
      data: dataCounts,
      deploys: deploysData,
      pointRadius: 3,
      pointHoverRadius: 4,
      pointBorderWidth: 1,
      pointHoverBorderWidth: 1.5,
      pointBorderColor: "#000000", // ab-black
      pointBackgroundColor: borderColors,
      deployAnnotationsDataset: true,
    };
  }

  private chartWithDeployAnnotationsTooltipLabel = (
    tooltipItem: ChartTooltipItem,
    data: any,
  ) => {
    const dataset = data.datasets[tooltipItem.datasetIndex];

    if (dataset.deployAnnotationsDataset) {
      const deploy = dataset.deploys[tooltipItem.index];
      const fixedGroupsCount = deploy.groupResolvedCount;
      return (
        `${deploy.username} deployed to ${deploy.environment},` +
        ` fixing ${fixedGroupsCount} ${pluralize(fixedGroupsCount, "error")}`
      );
    } else {
      return this.numberToHumanLabel(tooltipItem, data);
    }
  };

  private chartWithDeployAnnotationsTooltipTitle = (
    tooltipItems: ChartTooltipItem[],
    data: any,
  ) => {
    if (tooltipItems.length === 0) {
      return;
    }

    const tooltipItem = tooltipItems[0];
    const dataset = data.datasets[tooltipItem.datasetIndex];

    if (dataset.deployAnnotationsDataset) {
      const deploy = dataset.deploys[tooltipItem.index];

      // Temporary code to help us debug https://github.com/airbrake/airbrake-ng/issues/4541.
      if (!deploy) {
        this.airbrakeService.notifier.notify({
          error: "Deploy annotation tooltip error",
          params: { tooltipItems, tooltipItem, dataset },
        });
      }

      return formatDate(
        deploy.createdAt,
        this.DEPLOY_DATE_FORMAT,
        "en-US",
        "UTC",
      );
    } else {
      return this.dateFormatTooltipTitle(tooltipItems, data);
    }
  };

  private numberToHumanLabel = (tooltipItem: any, data: any) => {
    const label = data.datasets[tooltipItem.datasetIndex].label;
    return `${label}: ${formatNumberToHuman(tooltipItem.yLabel)}`;
  };

  private numberToHumanTick = (value: any, _index: any, _values: any) => {
    if (value % 1 === 0) {
      return formatNumberToHuman(value);
    }
  };

  private dateFormatTick = (value: any, _index: any, values: any) => {
    const duration = values[1] - values[0];

    if (duration <= this.MINUTE_DURATION) {
      return formatDate(value, this.TICK_TIME_FORMAT, "en-US", "UTC");
    } else if (duration <= this.HOUR_DURATION) {
      return formatDate(value, this.TICK_TIME_FORMAT, "en-US", "UTC");
    } else {
      return formatDate(value, this.TICK_DATE_FORMAT, "en-US", "UTC");
    }
  };

  private dateFormatTooltipTitle = (tooltipItems: any, data: any) => {
    if (tooltipItems.length === 0) {
      return;
    }

    const date = data.labels[tooltipItems[0].index];
    const duration = data.labels[1] - data.labels[0];

    if (duration <= this.MINUTE_DURATION) {
      return formatDate(date, this.TIME_FORMAT, "en-US", "UTC");
    } else if (duration <= this.HOUR_DURATION) {
      return formatDate(date, this.TIME_HOUR_FORMAT, "en-US", "UTC");
    } else {
      return formatDate(date, this.DATE_FORMAT, "en-US", "UTC");
    }
  };

  private filterTooltipItems = (tooltipItem: any, data: any) => {
    return data.datasets[tooltipItem.datasetIndex].showLine !== false;
  };

  // use regular function instead of arrow function to preserve 'this' as the
  // tooltip object
  private customTooltip = function (tooltipModel: any) {
    // Tooltip Element
    let tooltipEl = document.querySelector(".chartjs-tooltip") as HTMLElement;

    // Create element on first render
    if (tooltipEl == null) {
      tooltipEl = document.createElement("div");
      tooltipEl.className = "chartjs-tooltip";
      tooltipEl.innerHTML = "<table></table>";
      document.body.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    // Set Text
    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || [];
      const bodyLines = tooltipModel.body.map((item: any) => item.lines);

      let innerHtml = "<thead>";
      titleLines.forEach((title: string) => {
        innerHtml +=
          `<tr><th class="chartjs-tooltip-title">${title}` + "</th></tr>";
      });
      innerHtml += "</thead><tbody>";

      bodyLines.forEach((body: string, i: number) => {
        const colors = tooltipModel.labelColors[i];
        const style = `background: ${colors.backgroundColor}`;
        const span =
          '<span class="chartjs-tooltip-key" ' + `style="${style}"></span>`;
        innerHtml += `<tr><td>${span}${body}</td></tr>`;
      });
      innerHtml += "</tbody>";

      const tableRoot = tooltipEl.querySelector("table");
      tableRoot.innerHTML = innerHtml;
    }

    // Display, position, and set styles for font
    tooltipEl.style.opacity = "1";
    tooltipEl.style.position = "absolute";

    const position = this._chart.canvas.getBoundingClientRect();
    const chartArea = this._chart.chartArea;
    const posY = position.top + window.pageYOffset + chartArea.top;
    tooltipEl.style.top = `${posY}px`;

    const cursorX = this._eventPosition.x;
    const halfWidth = tooltipModel.width / 2;
    const midX = (chartArea.right + chartArea.left) / 2;
    const posX = position.left + window.pageXOffset + cursorX;
    const sideMargin = 10;

    if (cursorX >= midX) {
      tooltipEl.style.left = `${posX - halfWidth - sideMargin}px`;
    } else {
      tooltipEl.style.left = `${posX + halfWidth + sideMargin}px`;
    }

    tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
    tooltipEl.style.fontSize = `${tooltipModel.bodyFontSize}px`;
    tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
    tooltipEl.style.padding = `${tooltipModel.yPadding}px ${tooltipModel.xPadding}px`;
  };
}
